/**
 * Penalty mode gameplay helpers (used from legacy-app.ts).
 */
import {
    getPenaltyLevelDef,
    getNextPenaltyPublishedLevel,
    getPenaltyPublishedLevelIndices,
    hasPenaltyPublishedLevel,
    setRuntimePenaltyLevelsFromPayload,
    type PenaltyLevelDef,
} from "./penalty-levels";
import {
    drawPenaltyBall,
    penaltyTryCursorHitBall,
    penaltyYellowPointsUncollected,
    resetPenaltyBallAtNorm,
    stepPenaltyBall,
    type PenaltyBallState,
    type PenaltyGeomCtx,
} from "./penalty-mode";
import {
    speedrunLevelCheckpoints,
    speedrunPointGoalPosNorm,
} from "./speedrun-levels";

export {
    getPenaltyLevelDef,
    getNextPenaltyPublishedLevel,
    getPenaltyPublishedLevelIndices,
    hasPenaltyPublishedLevel,
    setRuntimePenaltyLevelsFromPayload,
    type PenaltyLevelDef,
};

export type PenaltyRuntimeState = {
    activeLevel: number;
    hitCount: number;
    ball: PenaltyBallState;
    ballPrev: { x: number; y: number };
    lastHitMs: { v: number };
    adminPlaytestActive: boolean;
};

export function createPenaltyRuntimeState(): PenaltyRuntimeState {
    return {
        activeLevel: 1,
        hitCount: 0,
        ball: { x: 0, y: 0, vx: 0, vy: 0, r: 12 },
        ballPrev: { x: 0, y: 0 },
        lastHitMs: { v: 0 },
        adminPlaytestActive: false,
    };
}

export function resetPenaltyBallFromLevel(
    st: PenaltyRuntimeState,
    w: number,
    h: number,
    ls: number,
) {
    const def = getPenaltyLevelDef(st.activeLevel);
    resetPenaltyBallAtNorm(st.ball, def.ballSpawn.nx, def.ballSpawn.ny, w, h, ls);
    st.ballPrev.x = st.ball.x;
    st.ballPrev.y = st.ball.y;
}

export function buildPenaltyGeomCtx(
    st: PenaltyRuntimeState,
    deps: {
        w: number;
        h: number;
        ls: number;
        tSec: number;
        wallSegDefs: PenaltyGeomCtx["wallSegDefs"];
        disabledWallIndices: Set<number>;
        movingDefs: PenaltyGeomCtx["movingDefs"];
        disabledMovingIndices: Set<number>;
        pointsNorm: PenaltyGeomCtx["pointsNorm"];
        pointsCollected: Set<number>;
        windDefs: PenaltyGeomCtx["windDefs"];
        goal: PenaltyGeomCtx["goal"];
        goalPx: PenaltyGeomCtx["goalPx"];
    },
): PenaltyGeomCtx {
    return {
        w: deps.w,
        h: deps.h,
        ls: deps.ls,
        tSec: deps.tSec,
        wallSegDefs: deps.wallSegDefs,
        disabledWallIndices: deps.disabledWallIndices,
        movingDefs: deps.movingDefs,
        disabledMovingIndices: deps.disabledMovingIndices,
        pointsNorm: deps.pointsNorm,
        pointsCollected: deps.pointsCollected,
        windDefs: deps.windDefs,
        goal: deps.goal,
        goalPx: deps.goalPx,
    };
}

export type PenaltyFrameInput = {
    st: PenaltyRuntimeState;
    ctx: PenaltyGeomCtx;
    cursorX: number;
    cursorY: number;
    cursorPrevX: number;
    cursorPrevY: number;
    cursorR: number;
    dt: number;
    nowMs: number;
};

export type PenaltyFrameResult = {
    hitRegistered: boolean;
    laserHit: boolean;
    goalReached: boolean;
    pointCollected: boolean;
};

export function tickPenaltyFrame(input: PenaltyFrameInput): PenaltyFrameResult {
    const { st, ctx, cursorX, cursorY, cursorPrevX, cursorPrevY, cursorR, dt, nowMs } = input;
    const out: PenaltyFrameResult = {
        hitRegistered: false,
        laserHit: false,
        goalReached: false,
        pointCollected: false,
    };
    if (
        penaltyTryCursorHitBall(
            st.ball,
            cursorX,
            cursorY,
            cursorPrevX,
            cursorPrevY,
            cursorR,
            80,
            nowMs,
            st.lastHitMs,
        )
    ) {
        st.hitCount += 1;
        out.hitRegistered = true;
    }
    st.ballPrev.x = st.ball.x;
    st.ballPrev.y = st.ball.y;
    const step = stepPenaltyBall(st.ball, ctx, dt, st.ballPrev.x, st.ballPrev.y);
    out.laserHit = step.laserHit;
    out.goalReached = step.goalReached;
    out.pointCollected = step.pointCollected;
    return out;
}

export function penaltyRespawnNormAfterLaser(st: PenaltyRuntimeState, deps: {
    lastCheckpointIndex: number | null;
    checkpointsNorm: ReturnType<typeof speedrunLevelCheckpoints>;
    tSec: number;
}): { nx: number; ny: number } {
    const def = getPenaltyLevelDef(st.activeLevel);
    if (deps.lastCheckpointIndex != null && deps.checkpointsNorm[deps.lastCheckpointIndex]) {
        const ck = deps.checkpointsNorm[deps.lastCheckpointIndex];
        const pos = speedrunPointGoalPosNorm(ck.nx, ck.ny, ck.pathMove, ck.move, deps.tSec);
        return { nx: pos.nx, ny: pos.ny };
    }
    return { ...def.ballSpawn };
}

export function formatPenaltyHits(n: number): string {
    const hits = Math.max(0, Math.floor(Number(n) || 0));
    return hits === 1 ? "1 hit" : `${hits} hits`;
}

export function readPenaltyBestJson(): Record<string, number> {
    try {
        const raw = localStorage.getItem("bestPenaltyHitsV1");
        if (!raw) return {};
        const o = JSON.parse(raw);
        return o && typeof o === "object" ? o : {};
    } catch {
        return {};
    }
}

export function readPenaltyBestHits(level: number): number {
    const v = Number(readPenaltyBestJson()[String(level)]);
    return Number.isFinite(v) && v > 0 ? v : 0;
}

export function maybeWritePenaltyBest(level: number, hits: number) {
    const h = Math.floor(hits);
    const prev = readPenaltyBestHits(level);
    if (!prev || h < prev) {
        const o = readPenaltyBestJson();
        o[String(level)] = h;
        localStorage.setItem("bestPenaltyHitsV1", JSON.stringify(o));
    }
}

export function drawPenaltyBallLayer(ctx: CanvasRenderingContext2D, ball: PenaltyBallState) {
    drawPenaltyBall(ctx, ball);
}

export function penaltyUncollectedYellowPoints(ctx: PenaltyGeomCtx): number {
    return penaltyYellowPointsUncollected(ctx);
}
